import {
  ArrowLeftRight,
  BookLock,
  BookOpen,
  CalendarClock,
  ChefHat,
  ClipboardCheck,
  ClipboardList,
  Contact,
  CookingPot,
  FileUp,
  LayoutDashboard,
  Moon,
  Network,
  Package,
  Percent,
  Scale,
  ShieldAlert,
  ShoppingCart,
  Smartphone,
  Tags,
  Trash2,
  Thermometer,
  TriangleAlert,
  Truck,
  type LucideIcon,
} from 'lucide-react';

import type { Permission } from './contracts.g';
import { tieneModulo, tieneRecetas, type Negocio } from './negocio';
import { mayuscula, vocabularioDe, type Vocabulario } from './vocabulario';

export type Seccion =
  | 'resumen'
  | 'insumos'
  | 'lotes'
  | 'reposicion'
  | 'recepcion'
  | 'carga-inicial'
  | 'compras'
  | 'proveedores'
  | 'produccion'
  | 'mermas'
  | 'cierre-de-noche'
  | 'conteo'
  | 'kardex'
  | 'valorizacion'
  | 'fuga'
  | 'costo-real'
  | 'cadena'
  | 'recetas'
  | 'cuarentena'
  | 'libro-de-recetas'
  | 'precios'
  | 'libro-de-controlados'
  | 'cadena-de-frio'
  | 'series';

interface Item {
  id: Seccion;
  texto: string | ((vocabulario: Vocabulario) => string);
  icono: LucideIcon;
  /**
   * El permiso sin el cual la pantalla solo enseñaría un «no autorizado». Del
   * contrato: uno mal escrito no compila, en vez de esconder la pantalla a todos.
   */
  permiso: Permission;
  /**
   * Lo que el negocio tiene que tener encendido. Sin él, la pantalla habla de
   * algo que el local no hace: a una farmacia, «Producción interna» le pide
   * sub-recetas que no existen.
   */
  requiere?: (negocio: Negocio) => boolean;
}

export interface ItemDelMenu {
  id: Seccion;
  texto: string;
  icono: LucideIcon;
}

/** Los cuatro grupos del diseño aprobado, en su orden. */
const MENU: { grupo: string; items: Item[] }[] = [
  {
    grupo: 'Estado',
    items: [
      { id: 'resumen', texto: 'Resumen', icono: LayoutDashboard, permiso: 'inventory.view' },
      {
        id: 'insumos',
        texto: (vocabulario) => mayuscula(vocabulario.insumos),
        icono: Package,
        permiso: 'inventory.view',
      },
      { id: 'lotes', texto: 'Lotes y vencimientos', icono: CalendarClock, permiso: 'inventory.view' },
      // Cada equipo con su IMEI (segunda vuelta de tecnología, 8 oct). Solo
      // donde se vende por serie: a una bodega no le dice nada.
      {
        id: 'series',
        texto: 'Equipos por serie',
        icono: Smartphone,
        permiso: 'inventory.view',
        requiere: (negocio) => tieneModulo(negocio, 'serials'),
      },
    ],
  },
  {
    grupo: 'Entradas',
    items: [
      { id: 'reposicion', texto: 'Qué comprar', icono: ClipboardCheck, permiso: 'inventory.purchase' },
      { id: 'recepcion', texto: 'Recepción', icono: Truck, permiso: 'inventory.manage' },
      // El stock con que arranca la tienda, desde una hoja (docs/15 §2.39).
      // Con el módulo de presentaciones: es de las tiendas que llegan con
      // miles de códigos; el restaurante no la ve.
      {
        id: 'carga-inicial',
        texto: 'Carga inicial',
        icono: FileUp,
        permiso: 'inventory.manage',
        requiere: (negocio) => tieneModulo(negocio, 'presentations'),
      },
      { id: 'compras', texto: 'Órdenes de compra', icono: ShoppingCart, permiso: 'inventory.view' },
      { id: 'proveedores', texto: 'Proveedores', icono: Contact, permiso: 'inventory.view' },
    ],
  },
  {
    grupo: 'Salidas',
    items: [
      {
        id: 'produccion',
        texto: 'Producción interna',
        icono: CookingPot,
        permiso: 'inventory.manage',
        requiere: tieneRecetas,
      },
      { id: 'mermas', texto: 'Mermas y bajas', icono: Trash2, permiso: 'inventory.manage' },
      // Lo que sobró y la barra, desde el celular al cerrar. Es de cocina:
      // una tienda no cierra la noche tirando el arroz ni midiendo botellas.
      {
        id: 'cierre-de-noche',
        texto: 'Cierre de la noche',
        icono: Moon,
        permiso: 'inventory.manage',
        requiere: tieneRecetas,
      },
      // Lo devuelto que espera revisión (migración 240). Solo existe con el
      // lote en la venta: sin él, lo devuelto vuelve al estante como siempre.
      // Mirarla es de quien mira el almacén; decidir, de quien firma.
      {
        id: 'cuarentena',
        texto: 'Cuarentena',
        icono: ShieldAlert,
        permiso: 'inventory.view',
        requiere: (negocio) => tieneModulo(negocio, 'lots_at_sale'),
      },
    ],
  },
  {
    grupo: 'Control',
    items: [
      { id: 'conteo', texto: 'Conteo físico', icono: ClipboardList, permiso: 'inventory.manage' },
      { id: 'kardex', texto: 'Kardex', icono: ArrowLeftRight, permiso: 'inventory.view' },
      { id: 'valorizacion', texto: 'Valorización', icono: Scale, permiso: 'inventory.view' },
      { id: 'fuga', texto: 'Lo que no cuadra', icono: TriangleAlert, permiso: 'report.control' },
      // El costo real es el food cost: lo que salió según receta contra lo
      // vendido. Sin recetas no hay con qué compararlo.
      {
        id: 'costo-real',
        texto: 'Costo real',
        icono: Percent,
        permiso: 'report.control',
        requiere: tieneRecetas,
      },
      { id: 'cadena', texto: 'La cadena', icono: Network, permiso: 'inventory.view' },
      // El libro que se le enseña a quien inspecciona (migración 239). Es de
      // quien responde por lo que se dispensa, el químico farmacéutico.
      {
        id: 'libro-de-recetas',
        texto: 'Libro de recetas',
        icono: BookOpen,
        permiso: 'inventory.approve',
        requiere: (negocio) => tieneModulo(negocio, 'prescriptions'),
      },
      // Las dos de la botica de la migración 273, con el módulo de la botica:
      // el libro de controlados es del químico, como el de recetas; la
      // cadena de frío es de quien mira la refrigeradora.
      {
        id: 'libro-de-controlados',
        texto: 'Libro de controlados',
        icono: BookLock,
        permiso: 'inventory.approve',
        requiere: (negocio) => tieneModulo(negocio, 'prescriptions'),
      },
      {
        id: 'cadena-de-frio',
        texto: 'Cadena de frío',
        icono: Thermometer,
        permiso: 'inventory.view',
        requiere: (negocio) => tieneModulo(negocio, 'prescriptions'),
      },
      {
        id: 'recetas',
        texto: 'Recetas y costeo',
        icono: ChefHat,
        permiso: 'inventory.recipe',
        requiere: tieneRecetas,
      },
      // Las escalas y listas por cliente, contra el costo del almacén
      // (migración 250). Solo con el módulo: el restaurante cobra su carta.
      {
        id: 'precios',
        texto: 'Precios',
        icono: Tags,
        permiso: 'price_list.manage',
        requiere: (negocio) => tieneModulo(negocio, 'price_lists'),
      },
    ],
  },
];

export const SECCIONES: readonly Seccion[] = MENU.flatMap((grupo) => grupo.items.map((item) => item.id));

/**
 * El menú de esta persona en este negocio: solo lo que su rol le deja abrir y
 * lo que el local tiene encendido.
 *
 * Quien recibe mercadería veía «Lo que no cuadra» y «Recetas y costeo», y al
 * entrar le contestaba el servidor que no. Un menú que ofrece lo que después
 * niega enseña a no fiarse de él. Lo mismo con el negocio: a una ferretería,
 * «Recetas y costeo» le ofrecía una pantalla que no tiene nada que hacer allí.
 */
export function menuDe(
  permisos: readonly string[],
  negocio: Negocio,
): { grupo: string; items: ItemDelMenu[] }[] {
  const vocabulario = vocabularioDe(negocio.rubro);

  return MENU.map((grupo) => ({
    grupo: grupo.grupo,
    items: grupo.items
      .filter((item) => permisos.includes(item.permiso))
      .filter((item) => item.requiere === undefined || item.requiere(negocio))
      .map((item) => ({
        id: item.id,
        icono: item.icono,
        texto: typeof item.texto === 'string' ? item.texto : item.texto(vocabulario),
      })),
  })).filter((grupo) => grupo.items.length > 0);
}

/** La sección pedida, o la primera que esta persona sí puede abrir. */
export function seccionPermitida(
  seccion: Seccion,
  permisos: readonly string[],
  negocio: Negocio,
): Seccion {
  const disponibles = menuDe(permisos, negocio).flatMap((grupo) => grupo.items.map((item) => item.id));
  return disponibles.includes(seccion) ? seccion : (disponibles[0] ?? 'resumen');
}
